import { CmdCalendar, CmdRangeFilterForm } from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import useKeyPress from '../../../hooks/useKeyPress';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { ICheckedTableItems, IFilterTripsOverview, ITripsOverviewTableData } from '../interfaces';
import { ESCAPE_KEY, ID_RESET, INVALID_DATE, RANGE_SELECT_CLASSNAME } from '../constants';
import { calendarTranslations } from '../../Forms/ReactHookFormFields/Calendar';
import {
  checkedTableItemsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IRangeDurationFilterProps {
  id: string;
}
interface IFormValues {
  from: Date;
  to: Date;
}

export default function RangeDurationFilter(props: IRangeDurationFilterProps): JSX.Element {
  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );

  const [valueFrom, setValueFrom] = React.useState<Date>(
    tableActionsFilters && tableActionsFilters.values && tableActionsFilters.values.min
      ? new Date(tableActionsFilters.values.min)
      : new Date(new Date().setHours(0, 0))
  );
  const [valueTo, setValueTo] = React.useState<Date>(
    tableActionsFilters && tableActionsFilters.values && tableActionsFilters.values.max
      ? new Date(tableActionsFilters.values.max)
      : new Date(new Date().setHours(23, 59))
  );
  const setFilterForRequest = useSetRecoilState<IFilterTripsOverview>(filterForRequestAtom);
  const { formatMessage: f } = useIntl();
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const { trackEvent } = useAnalytics();

  useOutsideClick(clickOutsideRef, (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const { id } = target;
    const elementClass = target.className;

    if (
      id !== props.id &&
      !RANGE_SELECT_CLASSNAME.find(
        (item) => typeof elementClass === 'string' && elementClass.includes(item)
      ) &&
      id !== ID_RESET &&
      ((tableActionsFilters && tableActionsFilters.isOpen) ||
        (tableActionsFilters &&
          tableActionsFilters.isActionChipsOpen &&
          tableActionsFilters.isFilterActive))
    ) {
      setTableActionsFilters(
        (tableActionFilter) =>
          tableActionFilter && {
            ...tableActionFilter,
            id: props.id,
            isOpen: false,
            isActionChipsOpen: false,
          }
      );
    }
  });

  useKeyPress(ESCAPE_KEY, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  const getTime = (value: string) => {
    const valueArr = value.split(':');
    if (valueArr.length === 3) {
      return `${valueArr[0]}:${valueArr[1]}`;
    }

    return value;
  };

  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<IFormValues>({
    defaultValues: {
      from: valueFrom,
      to: valueTo,
    },
    resolver: async (formData) => {
      if (formData.from && formData.to) {
        const formErrors: Record<string, { message: string }> = {};

        if (formData.from > formData.to) {
          formErrors.from = {
            message: `${f({ id: 'datepicker.spanFromToValidation' })}`,
          };
        }

        return {
          values: formErrors && Object.keys(formErrors).length ? {} : formData,
          errors: formErrors,
        };
      }
      return {
        values: formData,
        errors: {},
      };
    },
  });

  const onSubmit = (values: IFormValues) => {
    let defaultFrom = values.from;
    let defaultTo = values.to;

    if (!values.from && !values.to) {
      defaultFrom = new Date(new Date().setHours(0, 0));
      defaultTo = new Date(new Date().setHours(23, 59));
    }

    const timeFrom =
      dayjs(defaultFrom).format('HH:mm') !== INVALID_DATE
        ? dayjs(defaultFrom).format('HH:mm')
        : getTime(defaultFrom.toISOString());

    const timeTo =
      dayjs(defaultTo).format('HH:mm') !== INVALID_DATE
        ? dayjs(defaultTo).format('HH:mm')
        : getTime(defaultTo.toISOString());
    const [hourFrom, minutesFrom] = timeFrom.split(':').map((val) => Number(val));
    const convertFrom = dayjs.duration({ hours: hourFrom, minutes: minutesFrom }).asSeconds();
    const [hourTo, minutesTo] = timeTo.split(':').map((val) => Number(val));
    const convertTo = dayjs.duration({ hours: hourTo, minutes: minutesTo }).asSeconds();

    const data = {};

    data[props.id] = { min: Number(convertFrom), max: Number(convertTo) };
    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });

    setFilterForRequest((prevFilter) => ({
      ...prevFilter,
      ...data,
    }));

    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
          isFilterActive: true,
          values: {
            value: { min: convertFrom, max: convertTo },
            item: `${timeFrom} h - ${timeTo} h`,
            title: f({ id: 'table_list.columns.rideBook.duration' }),
            type: 'rangeSelect',
            min: String(values.from),
            max: String(values.to),
            unit: 'h',
            noteFrom: `hh:mm`,
            noteTo: `hh:mm`,
          },
        }
    );
  };

  const handleApply = () => {
    if (Object.keys(errors).length === 0) {
      trackEvent(['ride book', 'click', `Ride book apply range duration filter: ${props.id}`]);
      const values = getValues();
      onSubmit(values);
    }
  };

  const handleOnChangeTimeValueFrom = (value: Date) => {
    trackEvent([
      'ride book',
      'click',
      `Ride book change range duration filter from value: ${props.id}`,
    ]);
    const timeFrom =
      dayjs(value).format('HH:mm') !== INVALID_DATE ? new Date(new Date(value)) : value;
    setValue('from', new Date(timeFrom));
    setValueFrom(new Date(timeFrom));
    trigger();
  };

  const handleOnChangeTimeValueTo = (value: Date) => {
    trackEvent([
      'ride book',
      'click',
      `Ride book change range duration filter to value: ${props.id}`,
    ]);
    const timeTo =
      dayjs(value).format('HH:mm') !== INVALID_DATE ? new Date(new Date(value)) : value;
    setValue('to', new Date(timeTo));
    setValueTo(new Date(timeTo));
    trigger();
  };

  const handleReset = () => {
    trackEvent(['ride book', 'click', `Ride book reset range duration filter: ${props.id}`]);
    setValue('from', new Date(new Date().setHours(0, 0)));
    setValue('to', new Date(new Date().setHours(23, 59)));
    setValueFrom(new Date(new Date().setHours(0, 0)));
    setValueTo(new Date(new Date().setHours(23, 59)));
  };

  return (
    <div id={props.id} className="open" ref={clickOutsideRef}>
      {tableActionsFilters && tableActionsFilters.data && (
        <CmdRangeFilterForm
          id={props.id}
          submitCallback={handleApply}
          closeCallback={handleReset}
          submitBtnTitle={f({ id: 'table.apply' })}
        >
          <Controller
            name="from"
            control={control}
            render={({ field }) => (
              <CmdCalendar
                {...field}
                calendarTranslations={calendarTranslations(f)}
                id="from"
                data-cy="filter-input-time"
                label={f({ id: 'table_list.columnFilters.rangeSelect.min' })}
                onChangeCallback={handleOnChangeTimeValueFrom}
                mode="time"
                noteAfterInput="hh:mm"
                value={valueFrom}
                warningMessage={errors.from?.message as string}
              />
            )}
          />
          <Controller
            name="to"
            control={control}
            render={({ field }) => (
              <CmdCalendar
                {...field}
                calendarTranslations={calendarTranslations(f)}
                id="to"
                data-cy="filter-input-time"
                label={f({ id: 'table_list.columnFilters.rangeSelect.max' })}
                onChangeCallback={handleOnChangeTimeValueTo}
                mode="time"
                noteAfterInput="hh:mm"
                value={valueTo}
              />
            )}
          />
        </CmdRangeFilterForm>
      )}
    </div>
  );
}
